import { ApiError, authHeaders, type ApiErrorBody } from "@/services/api.client"
import { env } from "@/lib/env"
import { resolveApiUrl } from "@/lib/api-url"

/**
 * HTTP PUT for the OIA REST surface.
 *
 * WHY THIS FILE EXISTS: three OIA writes are PUTs in the backend contract
 * (case view, appeal particulars, an officer action on an authority row —
 * oia/schemas/api.py), and the shared `apiClient` has get / post / patch /
 * delete / upload but no put. The OIA team may not edit the shared client, so
 * this is the smallest faithful PUT: the same URL resolution, cookies, bearer
 * token and error mapping as `apiClient` (ApiError for a structured body; a
 * session 401 dispatches `auth:unauthorized`). When `apiClient.put` exists,
 * replace `putJson` with a one-line delegate to it.
 *
 * The bearer token comes from a provider the workspace registers from
 * AuthContext (the same source AuthProvider hands `configureApiClient`).
 */

function isApiErrorBody(obj: unknown): obj is ApiErrorBody {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof (obj as ApiErrorBody).code === "string" &&
    typeof (obj as ApiErrorBody).message === "string" &&
    typeof (obj as ApiErrorBody).severity === "string"
  )
}

function isSessionAuthFailure(detail: string | undefined): boolean {
  if (!detail) return false
  return (
    detail === "Not authenticated" ||
    detail.startsWith("Invalid access token:") ||
    detail.startsWith("API key") ||
    detail.startsWith("Invalid API key")
  )
}

function detailOf(body: unknown, status: number): string {
  if (typeof body === "object" && body !== null && "detail" in body) {
    const detail = (body as { detail: unknown }).detail
    return typeof detail === "string" ? detail : JSON.stringify(detail)
  }
  return `HTTP ${status}`
}

export async function putJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(resolveApiUrl(env.VITE_API_URL, path), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(body),
    credentials: "include",
  })
  let parsed: unknown
  try {
    parsed = await res.json()
  } catch {
    parsed = { detail: res.statusText }
  }
  if (res.ok) return (res.status === 204 ? undefined : parsed) as T

  if (res.status === 401 && isSessionAuthFailure(detailOf(parsed, res.status))) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"))
    throw new Error("Unauthorized")
  }
  if (isApiErrorBody(parsed)) throw new ApiError(parsed.message, parsed, res.status)
  // Keep the status in the message: callers tell a 409 (a run is live) from
  // other failures by it, as they do for apiClient errors.
  throw new ApiError(detailOf(parsed, res.status), {
    code: `http_${res.status}`,
    message: detailOf(parsed, res.status),
    severity: "error",
  }, res.status)
}
